import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import QRScanModal from '../QRScanModal';
import VoiceNoteRecorder from '../VoiceNoteRecorder';
import { pageStyles as ps } from './fieldFormat';

// These are `substrate_type` ENUM members, not labels: 'on another fungus'
// with spaces once sat in this list and made the eighth option a 500. The
// text shown for each is the collections catalogue's label for that member.
export const SUBSTRATES = [
  'live_trunk', 'dead_trunk', 'live_leaf', 'dead_leaf', 'dung', 'roots',
  'soil', 'on_another_fungus', 'carcass', 'other',
];
export const RANKS = ['species', 'genus', 'family', 'unknown'];
export const CONFIDENCES = ['certain', 'uncertain'];

/**
 * The details a specimen may carry, none of them required: the collector's
 * own name for it (given_name, 0110: Home's isolates table shows it beside
 * the place), who it is, what it grows on, notes, and (before it is sent) a
 * label and whether it is only a sighting.
 *
 * `value` is the batch's `details`; `onChange` gets the whole next value.
 * `tag` is useTagCheck's answer for the label. `sent` drops what can no
 * longer change once the record exists (the label, a sighting, "I can't
 * identify it" -- an identification posted later needs a name). `codeFixed`
 * shows the label and "only a sighting" without letting them change: a code
 * has been minted for this specimen, and the record must carry it. `canAsk`
 * says whether the dictionary may be searched now (see useTagCheck).
 *
 * `audio` ({ value, onChange }): voice notes recorded under the notes
 * (VoiceNoteRecorder), kept by the caller until it saves. `showNotes` false
 * leaves the notes to the caller (the hub's field form has a section of its
 * own for them), and `showObserveOnly` false leaves "only a sighting" to a
 * toggle of the caller's. `showTag` false drops the label altogether: an
 * external researcher's record takes no specimen code (0105), and the server
 * refuses one (code_not_allowed).
 */
export default function FieldDetailsForm({
  value, onChange, disabled = false, canMint = true, tag, sent = false, codeFixed = false, canAsk = () => true,
  audio = null, showNotes = true, showObserveOnly = true, showTag = true,
}) {
  const { t } = useTranslation(['field', 'collections', 'common']);
  const [taxonHits, setTaxonHits] = useState([]);
  const [scanning, setScanning] = useState(false);
  const search = useRef(0);
  const d = value || {};
  const patch = p => onChange({ ...d, ...p });

  // Only the answer to the latest search is shown.
  const searchTaxa = (q, rank) => {
    search.current += 1;
    const mine = search.current;
    if (!q || q.length < 2 || !canAsk()) return setTaxonHits([]);
    return api.get('/taxonomy/search', { params: { q, rank: rank || undefined } })
      .then((r) => { if (mine === search.current) setTaxonHits((r.data || []).slice(0, 8)); })
      .catch(() => { if (mine === search.current) setTaxonHits([]); });
  };

  const tagState = tag?.tagState || { status: 'idle' };
  const label = d.label || '';

  return (
    <>
      <fieldset style={ps.section} disabled={disabled}>
        <label style={ps.field}>
          <span style={ps.h2}>{t('details.givenName')}</span>
          <input style={ps.input} title={t('tip.givenName')} maxLength={120} autoComplete="off" dir="auto"
            data-testid="details-given-name" value={d.given_name || ''}
            onChange={e => patch({ given_name: e.target.value })} />
        </label>
      </fieldset>

      <fieldset style={ps.section} disabled={disabled} data-testid="details-identify">
        <legend style={{ ...ps.h2, padding: 0 }}>{t('details.identifyTitle')}</legend>
        <label style={ps.field}>
          <span style={ps.label}>{t('identify.rank')}</span>
          <select style={ps.input} value={d.rank || ''} title={t('tip.rank')} data-testid="details-rank"
            onChange={e => patch({ rank: e.target.value })}>
            <option value="">—</option>
            {RANKS.map(r => <option key={r} value={r}>{t(`common:rank.${r}`)}</option>)}
          </select>
        </label>
        <label style={ps.field}>
          <span style={ps.label}>{t('identify.taxon')}</span>
          <input style={ps.input} placeholder={t('identify.search')} title={t('tip.taxon')}
            data-testid="details-taxon" autoComplete="off" value={d.taxon_name || ''}
            onChange={(e) => {
              patch({ taxon_name: e.target.value });
              searchTaxa(e.target.value, d.rank);
            }} />
        </label>
        {taxonHits.length > 0 && (
          <ul style={ps.hits}>
            {taxonHits.map(h => (
              <li key={h.taxon_id || h.scientific_name}>
                <button type="button" style={ps.hitBtn} title={t('tip.taxonHit')}
                  onClick={() => {
                    patch({ taxon_name: h.scientific_name, rank: h.taxonomic_rank || d.rank || '' });
                    setTaxonHits([]);
                  }}>
                  {h.scientific_name}
                  {h.hebrew_name ? ` — ${h.hebrew_name}` : ''}
                </button>
              </li>
            ))}
          </ul>
        )}
        <label style={ps.field}>
          <span style={ps.label}>{t('details.confidence')}</span>
          <select style={ps.input} value={d.confidence || ''} title={t('tip.details.confidence')}
            data-testid="details-confidence" onChange={e => patch({ confidence: e.target.value })}>
            <option value="">—</option>
            {CONFIDENCES.map(c => <option key={c} value={c}>{t(`common:confidence.${c}`)}</option>)}
          </select>
        </label>
        {!sent && (
          <div style={ps.row}>
            <button type="button" style={ps.secondary} title={t('tip.cannot')} data-testid="details-cannot"
              onClick={() => { patch({ rank: 'unknown', taxon_name: '' }); setTaxonHits([]); }}>
              {t('identify.cannot')}
            </button>
          </div>
        )}
      </fieldset>

      <fieldset style={ps.section} disabled={disabled}>
        <legend style={{ ...ps.h2, padding: 0 }}>{t('details.placeTitle')}</legend>
        <label style={ps.field}>
          <span style={ps.label}>{t('place.substrate')}</span>
          <select style={ps.input} value={d.substrate || ''} title={t('tip.substrate')} data-testid="details-substrate"
            onChange={e => patch({ substrate: e.target.value })}>
            <option value="">—</option>
            {SUBSTRATES.map(s => <option key={s} value={s}>{t(`collections:substrate.${s}`)}</option>)}
          </select>
        </label>
        {/* Free text: what a host is, is the collector's word, not a list's. */}
        <label style={ps.field}>
          <span style={ps.label}>{t('place.host')}</span>
          <input style={ps.input} title={t('tip.host')} maxLength={200} autoComplete="off"
            data-testid="details-host" value={d.host_species || ''}
            onChange={e => patch({ host_species: e.target.value })} />
        </label>
      </fieldset>

      {!sent && showTag && (
        <fieldset style={ps.section} disabled={disabled || codeFixed} data-testid="details-tag">
          <legend style={{ ...ps.h2, padding: 0 }}>{t('tag.title')}</legend>
          {codeFixed && <p style={ps.hint} data-testid="details-tag-fixed">{t('details.tagFixed')}</p>}
          {showObserveOnly && (
            <label style={ps.check}>
              <input type="checkbox" style={ps.checkbox} checked={!!d.observeOnly} title={t('tip.details.observeOnly')}
                data-testid="details-observe-only" onChange={e => patch({ observeOnly: e.target.checked })} />
              <span>{t('details.observeOnly')}</span>
            </label>
          )}
          <div style={ps.row}>
            <input style={{ ...ps.input, flex: 1, minWidth: 0, width: 'auto' }} placeholder={t('tag.placeholder')}
              title={t('tip.tagCode')} data-testid="details-label" autoComplete="off" value={label}
              onChange={(e) => {
                patch({ label: e.target.value });
                // An answer about the code before this edit no longer applies.
                tag?.resetTag();
              }}
              onBlur={e => tag?.checkTag(e.target.value)} />
            <button type="button" style={ps.secondary} title={t('tip.tagScan')} data-testid="details-scan"
              onClick={() => setScanning(true)}>
              {t('tag.scan')}
            </button>
          </div>
          {!codeFixed && !d.observeOnly && canMint && !label.trim() && <p style={ps.hint}>{t('details.labelHelp')}</p>}
          {tagState.status === 'checking' && <p style={ps.hint}>{t('tag.checking')}</p>}
          {tagState.status === 'free' && <p style={ps.ok} data-testid="tag-free">{t('tag.free')}</p>}
          {tagState.status === 'specimenLabel' && (
            <p style={ps.ok} data-testid="tag-specimen">{t('tag.specimenLabel')}</p>
          )}
          {tagState.status === 'preprinted' && (
            <p style={ps.ok} data-testid="tag-preprinted">{t('tag.preprinted')}</p>
          )}
          {tagState.status === 'notPrinted' && (
            <p style={ps.hint} data-testid="tag-not-printed">{t('tag.notPrinted')}</p>
          )}
          {tagState.status === 'paired' && (
            <p style={ps.warn} data-testid="tag-paired">{t('tag.paired', { code: tagState.code })}</p>
          )}
          {tagState.status === 'inUse' && (
            <p style={ps.warn} data-testid="tag-in-use">{t('tag.inUse', { code: tagState.code })}</p>
          )}
          {tagState.status === 'reserved' && (
            <p style={ps.warn} data-testid="tag-reserved">{t('tag.reserved', { code: tagState.code })}</p>
          )}
        </fieldset>
      )}

      {(showNotes || audio) && (
        <fieldset style={ps.section} disabled={disabled}>
          {showNotes && (
            <label style={ps.field}>
              <span style={ps.h2}>{t('details.notesTitle')}</span>
              <textarea rows={3} style={{ ...ps.input, resize: 'vertical' }} title={t('tip.notes')}
                data-testid="details-notes" value={d.general_notes || ''}
                onChange={e => patch({ general_notes: e.target.value })} />
            </label>
          )}
          {audio && (
            <VoiceNoteRecorder value={audio.value} onChange={audio.onChange} disabled={disabled}
              testId="details-voice" />
          )}
        </fieldset>
      )}

      {!sent && !codeFixed && (
        <QRScanModal
          open={scanning}
          onClose={() => setScanning(false)}
          onDetected={(code) => {
            patch({ label: code });
            setScanning(false);
            tag?.checkTag(code);
          }}
        />
      )}
    </>
  );
}
